import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MeetupLocationPicker.css';

// Fix Leaflet marker icons - use default with fallback
const DefaultIcon = L.icon({
  iconUrl: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41"><path fill="%232ecc71" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.8 12.5 28.1 12.5 28.1S25 21.3 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="5" fill="white"/></svg>',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const MeetupLocationPicker = ({ onSelect, initialLocation = null, listingLocation = null, autoConfirm = false }) => {
  console.log('MeetupLocationPicker rendering with autoConfirm:', autoConfirm);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [nearbySpots, setNearbySpots] = useState([]);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualLocation, setManualLocation] = useState({ name: '', address: '' });

  // Initialize map
  useEffect(() => {
    console.log('MeetupLocationPicker mounted, mapRef:', mapRef.current);
    if (!mapRef.current || mapInstanceRef.current) {
      console.log('Skipping map init - ref missing or already initialized');
      return;
    }

    try {
      const defaultLat = listingLocation?.lat || 34.0522;
      const defaultLng = listingLocation?.lng || -118.2437;

      console.log('Initializing map at:', defaultLat, defaultLng);

      const map = L.map(mapRef.current).setView([defaultLat, defaultLng], 13);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      mapInstanceRef.current = map;

      // Click handler for map
      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        console.log('Map clicked at:', lat, lng);
        selectLocation(lat, lng, `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      });

      // Add initial marker if location provided
      if (initialLocation) {
        addMarker(initialLocation.lat, initialLocation.lng);
      }

      console.log('Map initialized successfully');

      return () => {
        map.off('click');
      };
    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to initialize map. Try manual entry instead.');
    }
  }, []);

  const addMarker = (lat, lng, title = 'Selected Location') => {
    try {
      if (!mapInstanceRef.current) {
        console.warn('Map not initialized yet');
        return;
      }

      // Remove old marker if exists
      if (markerRef.current) {
        try {
          mapInstanceRef.current.removeLayer(markerRef.current);
        } catch (e) {
          console.warn('Could not remove old marker:', e);
        }
      }

      // Add new marker
      const marker = L.marker([lat, lng], { icon: DefaultIcon })
        .bindPopup(title)
        .addTo(mapInstanceRef.current);

      marker.openPopup();
      markerRef.current = marker;

      // Zoom to marker
      mapInstanceRef.current.setView([lat, lng], 15);
      console.log('Marker added at:', lat, lng);
    } catch (err) {
      console.error('Error adding marker:', err);
    }
  };

  const searchAddress = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      console.log('Searching for:', query);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
        {
          headers: {
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const results = await response.json();
      console.log('Search results:', results);

      if (!results || results.length === 0) {
        setError('No locations found. Try a different search.');
        setSearchResults([]);
        setShowResults(false);
      } else {
        setSearchResults(results);
        setShowResults(true);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search addresses. Try manual entry.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const selectLocation = (lat, lng, name = 'Selected Location', address = null) => {
    try {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);

      console.log('selectLocation called with:', { lat: latNum, lng: lngNum, name });

      if (isNaN(latNum) || isNaN(lngNum)) {
        console.warn('Invalid coordinates:', { lat, lng });
        setError('Invalid location coordinates');
        return;
      }

      const location = {
        lat: latNum,
        lng: lngNum,
        name: name || `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`,
        address: address || name
      };

      // Clear any previous errors immediately
      setError('');
      setShowResults(false);
      setSearchQuery('');
      setManualEntry(false);

      // Add marker to map
      if (mapInstanceRef.current) {
        addMarker(latNum, lngNum, name);
      } else {
        console.warn('Map not ready yet, will add marker when map initializes');
      }

      // If autoConfirm, immediately call onSelect
      if (autoConfirm) {
        onSelect(location);
      } else {
        setSelectedLocation(location);
      }
    } catch (err) {
      console.error('Error selecting location:', err);
      setError('Error selecting location');
    }
  };

  const handleSearchResult = (result) => {
    selectLocation(
      result.lat,
      result.lon,
      result.display_name || `${result.lat}, ${result.lon}`
    );
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      onSelect(selectedLocation);
    }
  };

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          selectLocation(latitude, longitude, 'Your Current Location');
        },
        (err) => {
          setError('Location access denied. Use manual entry or search instead.');
          setManualEntry(true);
        }
      );
    } else {
      setError('Geolocation is not supported by your browser');
      setManualEntry(true);
    }
  };

  const handleClear = () => {
    setSelectedLocation(null);
    setSearchQuery('');
    setSearchResults([]);
    if (markerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  };

  return (
    <div className="meetup-location-picker">
      <div className="picker-container">
        {/* Search Section */}
        <div className="search-section">
          <h3>Select Meetup Location</h3>
          <p className="hint">Search for an address or click on the map</p>

          {!manualEntry && (
            <div style={{ position: 'relative' }}>
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search address or landmark..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchAddress(searchQuery)}
                  className="search-input"
                />
                <button
                  className="btn btn-search"
                  onClick={() => searchAddress(searchQuery)}
                  disabled={loading}
                >
                  {loading ? '🔄' : '🔍'}
                </button>
                <button
                  className="btn btn-location"
                  onClick={handleCurrentLocation}
                  title="Use my current location"
                >
                  📍
                </button>
                <button
                  className="btn btn-manual"
                  onClick={() => {
                    setManualEntry(true);
                    setError('');
                    setShowResults(false);
                  }}
                  title="Enter location manually"
                >
                  ✏️
                </button>
              </div>

              {showResults && (
                <div className="search-results">
                  {searchResults.length > 0 ? (
                    searchResults.map((result, idx) => (
                      <div
                        key={idx}
                        className="search-result-item"
                        onClick={() => {
                          console.log('Clicked result:', result);
                          handleSearchResult(result);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="result-icon">📍</div>
                        <div className="result-info">
                          <div className="result-name">
                            {result.name || result.display_name?.split(',')[0] || 'Location'}
                          </div>
                          <div className="result-address">
                            {result.display_name?.substring(0, 80) || 'No address'}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '12px 16px', color: '#999', fontSize: '13px' }}>
                      No results found
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          {manualEntry && (
            <div className="manual-entry-form">
              <h4>Enter Location Manually</h4>
              <div className="form-group">
                <label>Location Name</label>
                <input
                  type="text"
                  placeholder="e.g., Starbucks on Main St"
                  value={manualLocation.name}
                  onChange={(e) => setManualLocation(p => ({ ...p, name: e.target.value }))}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  placeholder="e.g., 123 Main St, Portland, OR 97201"
                  value={manualLocation.address}
                  onChange={(e) => setManualLocation(p => ({ ...p, address: e.target.value }))}
                  className="form-input"
                />
              </div>
              <div className="form-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (manualLocation.name && manualLocation.address) {
                      selectLocation(0, 0, manualLocation.name, manualLocation.address);
                      setManualEntry(false);
                    }
                  }}
                  disabled={!manualLocation.name || !manualLocation.address}
                >
                  Use This Location
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setManualEntry(false);
                    setManualLocation({ name: '', address: '' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="map-wrapper">
          <div ref={mapRef} className="map-container" />
        </div>

        {/* Selected Location Info - only show if not autoConfirm */}
        {selectedLocation && !autoConfirm && (
          <div className="location-info">
            <div className="location-header">
              <h4>📍 Selected Meetup Spot</h4>
              <button className="btn-clear" onClick={handleClear}>✕</button>
            </div>
            <div className="location-details">
              <div className="detail-row">
                <span className="label">Name:</span>
                <span className="value">{selectedLocation.name}</span>
              </div>
              <div className="detail-row">
                <span className="label">Coordinates:</span>
                <span className="value">{selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}</span>
              </div>
              {selectedLocation.address && (
                <div className="detail-row">
                  <span className="label">Address:</span>
                  <span className="value">{selectedLocation.address}</span>
                </div>
              )}
            </div>
            <button className="btn btn-primary btn-full" onClick={handleConfirm}>
              ✓ Confirm This Location
            </button>
          </div>
        )}

        {!selectedLocation && (
          <div className="no-selection">
            <p>👆 Click on the map or search to select a meetup location</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetupLocationPicker;
